import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import ModifyDisbursementRow from './ModifyDisbursementRow'
import ViewDisbursementRow from './ViewDisbursementRow'
import { DisbursementMutationDto, DisbursementOverviewDto, DisbursementType } from '../../../config/registrationConfig'
import RegistrationList, { registrationListColumns, RegistrationListProps } from '../Registration/RegistrationList'
import { IColumn } from '@fluentui/react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import useDisbursementTypes from '../../../hooks/useDisbursementTypes'

export type DisbursementListProps = RegistrationListProps<DisbursementOverviewDto>

const getColumns = (translate: (key: string) => string, exclude: string[]): IColumn[] =>
    [
        registrationListColumns.user(),
        registrationListColumns.date(),
        {
            key: 'type',
            fieldName: 'type',
            name: translate('base.table.header.type'),
            minWidth: 120,
            maxWidth: 120,
        },
        registrationListColumns.description(translate),
        registrationListColumns.price('rate', translate('base.table.header.rate'), false),
        {
            key: 'vat',
            fieldName: 'vatType',
            name: translate('base.table.header.vat'),
            minWidth: 96,
            maxWidth: 96,
        },
        registrationListColumns.price('total', translate('base.table.header.total'), true),
        registrationListColumns.invoice(translate),
        registrationListColumns.actions(),
    ].filter(column => !exclude.includes(column.key))

const getValidationSchema = (disbursementTypes: DisbursementType[], translate: (key: string) => string) =>
    Yup.object().shape({
        userId: Yup.string().trim().required(translate('file.pivot.registration.form.userid.empty')),
        date: Yup.string().trim().required(translate('file.pivot.registration.form.date.empty')),
        type: Yup.string()
            .trim()
            .when({
                is: () => disbursementTypes.length > 0,
                then: schema => schema.required(translate('file.pivot.registration.form.type.empty')),
                otherwise: schema => schema.nullable(),
            })
            .test(
                'is-valid-type',
                translate('file.pivot.registration.form.type.empty'),
                value => disbursementTypes.length === 0 || value !== undefined,
            ),
        description: Yup.string().trim().required(translate('file.pivot.registration.form.description.empty')),
        vatType: Yup.string().trim().required(translate('file.pivot.registration.form.vattype.empty')),
    })

export default function DisbursementList(props: DisbursementListProps): ReactElement {
    const { t: translate } = useTranslation()

    const { disbursementTypes } = useDisbursementTypes()
    const columns = useMemo(() => getColumns(translate, [(disbursementTypes || []).length === 0 && 'type'].filter(Boolean)), [disbursementTypes])

    return (
        <RegistrationList<DisbursementOverviewDto, DisbursementMutationDto>
            detailsListProps={{ columns }}
            {...props}
            listProps={{ ...props.listProps }}
            baseUrl="/registration/disbursement"
            initialValues={{
                userId: props.listProps.file.owner.id,
                type: disbursementTypes?.length === 1 ? disbursementTypes[0].id : undefined,
                vatType: disbursementTypes?.length === 1 ? disbursementTypes[0].vatType : props.listProps.file.vatType,
            }}
            mapItem={(item: DisbursementOverviewDto) => ({ type: item.type, vatType: item.vatType })}
            viewRow={ViewDisbursementRow}
            modifyRow={ModifyDisbursementRow}
            validationSchema={getValidationSchema(disbursementTypes, translate)}
        />
    )
}
